import { useRef } from 'react';
import toast from 'react-hot-toast';
import Button from './Button';

/**
 * Image upload control with preview + change/remove actions.
 *
 * Public interface: `src` (current image url) plus `onUpload(file)` and
 * optional `onRemove()`. Hides the hidden file-input wiring, image-type
 * guard, and the preview / placeholder / actions layout so both the user
 * avatar and the organization logo can share one control.
 *
 * FormData/Content-Type is the caller's concern: `onUpload` receives the raw
 * File; upload helpers in lib/api.js handle the multipart request.
 *
 * Props:
 *   src       — current image URL (falsy => placeholder)
 *   onUpload  — (file: File) => void | Promise; called after type validation
 *   onRemove  — optional () => void | Promise; renders a Remove action when set
 *   busy      — disables actions and shows uploading state
 *   alt       — alt text for the preview image
 *   label     — accessible label for the change action (default 'photo')
 *   shape     — 'circle' (avatar) | 'square' (logo)  (default 'circle')
 *   initials  — short text shown in the placeholder when there is no image
 */
export default function AvatarUploader({
  src,
  onUpload,
  onRemove,
  busy = false,
  alt = '',
  label = 'photo',
  shape = 'circle',
  initials = '',
}) {
  const inputRef = useRef(null);

  const handleChange = (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file (e.g. PNG, JPG)');
      return;
    }
    onUpload?.(file);
  };

  return (
    <div className={['avatar-uploader', `avatar-uploader--${shape}`].join(' ')}>
      <div className="avatar-uploader-preview" aria-hidden={!src}>
        {src ? (
          <img className="avatar-uploader-img" src={src} alt={alt} />
        ) : (
          <span className="avatar-uploader-placeholder">{initials || '—'}</span>
        )}
      </div>

      <div className="avatar-uploader-actions">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="avatar-uploader-input"
          onChange={handleChange}
          disabled={busy}
        />
        <Button
          type="button"
          variant="secondary"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
        >
          {busy ? 'Uploading…' : src ? `Change ${label}` : `Upload ${label}`}
        </Button>
        {src && onRemove && (
          <Button type="button" variant="ghost" onClick={() => onRemove()} disabled={busy}>
            Remove
          </Button>
        )}
      </div>
    </div>
  );
}
